import LoadingBlock from '@/components/feedback/LoadingBlock';
import { useApiCall } from '@/hooks/useApiCall';
import { FunctionComponent } from 'react';
import { toast } from 'react-toastify';
import { userService } from '../../../api/users/users.service';
import { UserModel } from '../../../types/UserModel';
import UserFormLogic from './UserFormLogic';
import { UserFormModel } from './UserFormModel';
import { useNavigate } from 'react-router-dom';
import { isNumber } from 'lodash-es';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { useUser } from '@/modules/users/contexts/UserContext';
import { useTranslation } from '@/lib';
import { UserType } from '../../../api/users/users.contracts';
import { ApiResponseCode } from '@/lib/api/api-response-code';
import { roleService } from '@/modules/users/submodules/roles/api/roles.service';
import { useRoles } from '@/modules/users/submodules/roles/hooks/useRoles';
import { APPLICATION_ADMIN_ROLE_NAME } from '@/modules/users/submodules/roles/api/roles.contracts';

interface UserFormApiProps {
  isCreate?: boolean;
  userId?: number;
}

const UserFormApi: FunctionComponent<UserFormApiProps> = ({ isCreate, userId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasUserEditPermission = useUser().hasPermission(Permission.USER_EDIT);
  // Must stay above the conditional useApiCall further down, or hook order breaks.
  const { roles } = useRoles();

  const handleSubmit = async (data: UserFormModel) => {
    if (!hasUserEditPermission) {
      toast.error(t('no_permission_to_edit_users'));
      return { success: false };
    }

    // For guest users, clear roleIds (backend will assign default viewer role)
    const roleIds = data.type === UserType.GUEST ? [] : data.roleIds;

    // Administrators are never location-scoped; the form hides the picker for them, so make
    // sure nothing lingers in the payload either.
    const isAdminUser = roles.some((role) => roleIds?.includes(role.dto.id) && role.dto.name === APPLICATION_ADMIN_ROLE_NAME);
    const locationIds = isAdminUser ? [] : data.locationIds;

    // Process data
    if (isCreate) {
      const response = await userService.createUser({
        email: data.email,
        name: data.name,
        password: data.password as string,
        type: data.type,
        isActive: data.isActive ?? true,
        roleIds: roleIds,
        locationIds: locationIds,
      });

      if (!response.isSuccess) {
        if (response.responseCode === ApiResponseCode.USER_EMAIL_EXISTS || response.responseCode === ApiResponseCode.EMAIL_IN_USE) {
          return {
            success: false,
            fieldErrors: {
              email: t('user_email_already_exists'),
            },
          };
        }
        toast(t('error_creating_user'), {
          type: 'error',
        });
        return { success: false };
      } else {
        // For guest users, update the role permissions
        if (data.type === UserType.GUEST && data.permissions && response.payload) {
          const createdUser = response.payload;
          if (createdUser.roles && createdUser.roles.length > 0) {
            const roleId = createdUser.roles[0].id;
            await roleService.updateRolePermissions({
              roleId,
              permissions: data.permissions,
            });
          }
        }
        toast.success(t('user_created'));
        navigate('/app/configuration/users/');
        return { success: true };
      }
    } else if (isNumber(userId)) {
      const response = await userService.updateUser(userId, {
        name: data.name,
        password: data.password as string,
        type: data.type,
        isActive: data.isActive ?? true,
        roleIds: roleIds,
        locationIds: locationIds,
      });

      if (!response.isSuccess) {
        toast(t('error_updating_user'), {
          type: 'error',
        });
        return { success: false };
      } else {
        // For guest users, update the role permissions
        if (data.type === UserType.GUEST && data.permissions && response.payload) {
          const updatedUser = response.payload;
          if (updatedUser.roles && updatedUser.roles.length > 0) {
            const roleId = updatedUser.roles[0].id;
            await roleService.updateRolePermissions({
              roleId,
              permissions: data.permissions,
            });
          }
        }
        toast.success(t('user_updated'));
        navigate('/app/configuration/users/');
        return { success: true };
      }
    }

    return { success: true };
  };

  if (isCreate || !userId) {
    const newUser: UserFormModel = {
      email: '',
      name: '',
      type: UserType.OPERATOR,
      isActive: true,
      roleIds: [],
      password: '',
      locationIds: [],
      permissions: [],
    };
    return <UserFormLogic initialValues={newUser} onSubmit={handleSubmit} isCreate />;
  } else {
    const { data, isLoading, isError } = useApiCall<UserModel>(() => userService.getUserById(userId));

    // return early if initial form data isn't loaded
    if (isLoading) return <LoadingBlock />;
    if (isError || !data) return <div>{t('no_data_error')}</div>;

    return (
      <UserFormLogic
        initialValues={{
          email: data.email,
          name: data.name,
          type: data.type,
          isActive: data.isActive,
          roleIds: data.roles.map((role) => role.dto.id) as [number, ...number[]],
          password: data.password,
          locationIds: data.locationIds,
          // For guest users, load permissions from their role
          permissions: data.type === UserType.GUEST && data.roles.length > 0 ? data.roles[0].dto.permissions.map((p) => p.name) : [],
        }}
        onSubmit={handleSubmit}
        isCreate={isCreate}
        userId={userId}
        userData={data}
      />
    );
  }
};

export default UserFormApi;
